import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { decodeBlobError } from '../../services/blob-error.util';
import { ErrorService } from '../../services/error.service';
import { FileService } from '../../services/file.service';
import { OrderService } from '../../services/order.service';
import { ParcelService } from '../../services/parcel.service';
import { RouteTrackingService } from '../../services/route-tracking.service';
import { SpinnerService } from '../../services/spinner.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-print-route-tracking-shipping-label-modal',
  templateUrl: './print-route-tracking-shipping-label-modal.component.html',
  styleUrls: ['./print-route-tracking-shipping-label-modal.component.css']
})
export class PrintRouteTrackingShippingLabelModalComponent implements OnInit {
  public form: UntypedFormGroup;
  public shippingLabels: any[] = [];
  public printing = false;

  @ViewChild(MatSelect) labelSelect: MatSelect;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _dialogRef: MatDialogRef<PrintRouteTrackingShippingLabelModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _orderService: OrderService,
    private _parcelService: ParcelService,
    private _routeTrackingService: RouteTrackingService,
    private _fileService: FileService,
    private _spinnerService: SpinnerService,
    private _errorService: ErrorService,
    private _userService: UserService
  ) {}

  // XD-60834: only used when the response is NOT a PDF — a PDF still opens in a new window and is never
  // named. Names the thing that was printed so a folder of downloaded .zpl files is readable.
  private shippingLabelFileName(): string {
    switch (this.data.mode) {
      case 'bulkOrders': return 'Shipping Labels';
      case 'route':      return `Shipping Labels - Route ${this.data.routeId}`;
      case 'routeStop':  return `Shipping Labels - Route Stop ${this.data.routeStopId}`;
      default:           return `Shipping Labels - Parcel ${this.data.parcelId}`;
    }
  }

  ngOnInit() {
    this.form = this._formBuilder.group({
      selectedShippingLabel: [null, [Validators.required]]
    });

    this._userService.user$.pipe(take(1)).subscribe(user => {
      // data.mode values: 'bulkOrders' | 'route' | 'routeStop' | 'parcel'
      // 'parcel' falls through to 'routeParcel' context (distinguishes route-parcel from OD labels).
      // 'bulkOrders' maps to 'bulkOrder' context (backend returns bulk-order shipping labels).
      const context = this.data.mode === 'bulkOrders' ? 'bulkOrder'
        : this.data.mode === 'route' ? 'route'
        : this.data.mode === 'routeStop' ? 'routeStop'
        : 'routeParcel';
      const defaultLabelId = this.data.mode === 'bulkOrders' ? null
        : context === 'route' ? user?.inetUser?.routeShippingLabelId
        : context === 'routeStop' ? user?.inetUser?.routeStopShippingLabelId
        : user?.inetUser?.routeParcelShippingLabelId;
      this._orderService.getAvailableShippingLabels(context).subscribe(response => {
        if (response && response.length > 0) {
          this.shippingLabels = response;
          const defaultLabel = defaultLabelId
            ? response.find(l => l.procedureId === defaultLabelId)
            : null;
          const fallback = response[0].procedureId;
          this.form.controls.selectedShippingLabel.setValue(
            defaultLabel ? defaultLabel.procedureId : fallback
          );
          this.form.updateValueAndValidity();
        }
      }, error => {
        this._errorService.error(error, 'getAvailableShippingLabels Failed', 'modal');
      });
    });
  }

  print() {
    // Each call is a full server-side label render, and on a 100+ order report that is expensive.
    // Without this, rapid clicks fire one POST and open one PDF tab per click.
    if (this.form.invalid || this.printing) return;
    this.printing = true;
    const shippingLabelId = this.form.value.selectedShippingLabel;

    this._spinnerService.startSpinner();

    let printObservable: Observable<Blob>;
    if (this.data.mode === 'bulkOrders') {
      printObservable = this._orderService.bulkPrintOrderShippingLabels(this.data.reportData, shippingLabelId);
    } else if (this.data.mode === 'route') {
      printObservable = this._parcelService.printAllShippingLabelsForRoute(this.data.routeId, this.data.postDate, shippingLabelId);
    } else if (this.data.mode === 'routeStop') {
      printObservable = this._routeTrackingService.getRouteStopShippingLabelsPDF(this.data.routeStopId, shippingLabelId);
    } else {
      printObservable = this._parcelService.printParcelShippingLabels(this.data.parcelId, shippingLabelId);
    }

    printObservable.subscribe((response: any) => {
      this.printing = false;
      this._spinnerService.stopSpinner();
      // XD-60834: all four modes above can resolve to a ZPL-format procedure, so the delivery follows
      // the response's content type. A PDF still opens in a new window exactly as before.
      this._fileService.deliverShippingLabel(response, this.shippingLabelFileName());
      this.close();
    }, error => {
      this.printing = false;
      this._spinnerService.stopSpinner();
      // Every print observable here is Observable<Blob>, so `error.error` is a Blob and
      // ErrorService renders its literal fallback for 403, 404 and 500 alike — decode it first so
      // the backend's own message reaches the user.
      //
      // Structured, not interpolated: the url carries shippingLabelId, dates and top, so inlining it
      // would make every failure a different log message. ErrorService logs a non-string log text
      // as attributes, so the constant is the `text.text` attribute (mode/status/url beside it),
      // not the log message where the string call sites put theirs.
      const context = {
        text: 'printShippingLabels Failed',
        mode: this.data.mode,
        status: error?.status,
        url: error?.url
      };
      decodeBlobError(error, decoded => {
        const errorDialog = this._errorService.error(decoded, context, 'modal');
        // This modal stays open behind the error so the user can retry with a different label, but
        // the CDK leaves focus on BODY when the error dialog closes — a keyboard user would have to
        // Tab from the top of the document. `?.` because ErrorService returns nothing for a toast.
        errorDialog?.afterClosed().subscribe(() => this.labelSelect?.focus());
      });
    });
  }

  close() {
    this._dialogRef.close();
  }
}
